import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, map, switchMap } from 'rxjs';
import { CompanyProductStatusMonitor } from 'src/app/models/company_product_status_monitor.model';
import { CompanyProductUserMember } from 'src/app/models/company_product_user_member.model';
import { MessagingConversation } from 'src/app/models/messaging_conversation.model';
import { User } from 'src/app/models/user.model';
import { ApiService, PermissionsService } from 'src/app/services';
import { CompanyProductPageIdentityComponent } from '../company-product-page-identity/company-product-page-identity.component';
import { injectCompanyProductFromDetail, resolveCompanyProductIdFromParamMap } from '../company-product-detail.component';
import {
  ConfigureStatusMonitorNotifiablesDialogComponent,
  DefaultStatusMonitorNotifiable,
  formatMessagingConversationLabel,
  formatUserLabel,
} from './configure-status-monitor-notifiables-dialog/configure-status-monitor-notifiables-dialog.component';

@Component({
  selector: 'app-company-product-status-monitors',
  templateUrl: './company-product-status-monitors.component.html',
  styleUrls: ['./company-product-status-monitors.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CompanyProductPageIdentityComponent, MatButton, MatIcon],
})
export class CompanyProductStatusMonitorsComponent {
  private readonly activated_route = inject(ActivatedRoute);
  private readonly api = inject(ApiService);
  private readonly dialog = inject(MatDialog);
  private readonly permissions = inject(PermissionsService);

  readonly company_product = injectCompanyProductFromDetail();
  readonly company_product_id = resolveCompanyProductIdFromParamMap(this.activated_route.parent!.snapshot.paramMap);
  readonly refresh_tick = signal(0);

  readonly monitors = toSignal(
    toObservable(this.refresh_tick).pipe(
      switchMap(() =>
        this.api
          .getCompanyProductStatusMonitors(this.company_product_id, {
            with: 'user_notifiables,messaging_conversations,company_product_status_monitor_endpoints,company_product_status_monitor_last_exec',
          })
          .pipe(map(response => response.data))
      )
    ),
    { initialValue: [] as CompanyProductStatusMonitor[] }
  );

  readonly default_notifiables = toSignal(
    combineLatest([
      this.api.getActiveCompanyProductMembersWithBusiness(String(this.company_product_id)),
      this.api.getCompanyProduct(String(this.company_product_id), {
        with: 'internal_team_messaging_conversation',
      }),
    ]).pipe(
      map(([members, company_product]) =>
        buildDefaultStatusMonitorNotifiables(members.bisual_users ?? [], company_product.internal_team_messaging_conversation ?? null)
      )
    ),
    { initialValue: [] as DefaultStatusMonitorNotifiable[] }
  );

  readonly can_configure_notifiables = computed((): boolean => {
    const company_product_id = String(this.company_product_id);

    return (
      this.permissions.isAdmin() ||
      this.permissions.isAccountManagerInCompanyProduct(company_product_id) ||
      this.permissions.isBisualProductionTeamInCompanyProductWithRole(company_product_id, 'product-manager')
    );
  });

  readonly has_bot_not_member_conversation = computed((): boolean => {
    const default_conversations_missing = this.default_notifiables().some(
      item => item.kind === 'conversation' && item.bot_is_member === false
    );
    if (default_conversations_missing) {
      return true;
    }

    return this.monitors().some(monitor =>
      (monitor.messaging_conversations ?? []).some(conversation => conversation.bot_is_member === false)
    );
  });

  readonly serialized_monitors = computed((): StatusMonitorRow[] => {
    const default_notifiables = this.default_notifiables();

    return this.monitors().map(monitor => serializeStatusMonitorRow(monitor, default_notifiables));
  });

  openConfigureDialog(monitor: CompanyProductStatusMonitor): void {
    if (!this.can_configure_notifiables()) {
      return;
    }

    const company_product = this.company_product();
    if (company_product == null) {
      return;
    }

    this.dialog
      .open(ConfigureStatusMonitorNotifiablesDialogComponent, {
        width: '560px',
        data: {
          monitor,
          company_product_id: this.company_product_id,
          company_id: company_product.company_id,
          default_notifiables: this.default_notifiables(),
        },
      })
      .afterClosed()
      .subscribe((updated: CompanyProductStatusMonitor | undefined): void => {
        if (updated == null) {
          return;
        }

        this.refresh_tick.update(tick => tick + 1);
      });
  }

  formatUserLabel = formatUserLabel;
  formatMessagingConversationLabel = formatMessagingConversationLabel;
}

interface StatusMonitorRow {
  monitor: CompanyProductStatusMonitor;
  monitor_id_label: string;
  status_label: string;
  status_class: 'ok' | 'warn' | 'muted';
  is_active_label: string;
  exec_interval_label: string;
  endpoints_count_label: string;
  default_notifiables: DefaultStatusMonitorNotifiable[];
  user_notifiables: User[];
  messaging_conversations: MessagingConversation[];
  has_configured_notifiables: boolean;
  has_any_notifiables: boolean;
}

export function buildDefaultStatusMonitorNotifiables(
  bisual_members: CompanyProductUserMember[],
  internal_team_conversation: MessagingConversation | null
): DefaultStatusMonitorNotifiable[] {
  const items: DefaultStatusMonitorNotifiable[] = [];
  const seen_user_ids = new Set<number>();

  bisual_members.forEach(member => {
    const slug = member.company_product_user_member_role?.slug;
    const user = member.user;
    if (user == null || seen_user_ids.has(user.id)) {
      return;
    }

    if (slug === 'tech-lead') {
      seen_user_ids.add(user.id);
      items.push({
        kind: 'user',
        id: user.id,
        label: formatUserLabel(user),
        reason_label: 'Tech Lead',
      });
      return;
    }

    if (slug === 'product-manager') {
      seen_user_ids.add(user.id);
      items.push({
        kind: 'user',
        id: user.id,
        label: formatUserLabel(user),
        reason_label: 'Product Manager',
      });
    }
  });

  if (internal_team_conversation != null) {
    items.push({
      kind: 'conversation',
      id: internal_team_conversation.id,
      label: internal_team_conversation.display_name,
      reason_label: 'Canal interno',
      bot_is_member: internal_team_conversation.bot_is_member,
    });
  }

  return items;
}

function serializeStatusMonitorRow(
  monitor: CompanyProductStatusMonitor,
  default_notifiables: DefaultStatusMonitorNotifiable[]
): StatusMonitorRow {
  const default_user_ids = new Set(default_notifiables.filter(item => item.kind === 'user').map(item => item.id));
  const default_conversation_ids = new Set(default_notifiables.filter(item => item.kind === 'conversation').map(item => item.id));

  const user_notifiables = (monitor.user_notifiables ?? []).filter(user => !default_user_ids.has(user.id));
  const messaging_conversations = (monitor.messaging_conversations ?? []).filter(
    conversation => !default_conversation_ids.has(conversation.id)
  );
  const last_exec = monitor.company_product_status_monitor_last_exec;
  const endpoints_count = monitor.company_product_status_monitor_endpoints?.length ?? 0;

  let status_label = 'Sin ejecuciones';
  let status_class: StatusMonitorRow['status_class'] = 'muted';

  if (last_exec != null) {
    if (last_exec.status === 'healthy') {
      status_label = 'Operativo';
      status_class = 'ok';
    } else if (last_exec.status === 'unhealthy') {
      status_label = 'Caída';
      status_class = 'warn';
    } else {
      status_label = last_exec.status;
    }
  }

  const has_configured_notifiables = user_notifiables.length + messaging_conversations.length > 0;

  return {
    monitor,
    monitor_id_label: `#${monitor.id}`,
    status_label,
    status_class,
    is_active_label: monitor.is_active ? 'Activo' : 'Inactivo',
    exec_interval_label: `Cada ${monitor.exec_every_n_minutes} min`,
    endpoints_count_label: `${endpoints_count} endpoint${endpoints_count === 1 ? '' : 's'}`,
    default_notifiables,
    user_notifiables,
    messaging_conversations,
    has_configured_notifiables,
    has_any_notifiables: default_notifiables.length > 0 || has_configured_notifiables,
  };
}
